/**
 * The answers of a finished `qa` run: question → answer → citation chips,
 * filterable by status; expanding a row shows the quoted evidence. Downloads:
 * the shared PDF report and the editable DOCX.
 */

import { Fragment, useState } from "react";
import {
  ActionIcon,
  Badge,
  Blockquote,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight, IconFileTypeDocx } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  AdvisoryBanner,
  CitationChip,
  ReportDownloadButton,
  citationLabel,
} from "@/features/solutions/shared";
import { downloadRunOutput, type RunDetailOut } from "@/shared/api/solutionRuns";
import { toastApiError } from "@/shared/ui/toast";
import {
  QA_STATUS_COLOR,
  QA_STATUS_LABEL,
  answeredByLabel,
  type QaResult,
  type QaStatus,
} from "./qaTypes";

type Filter = "all" | QaStatus;

interface Props {
  run: RunDetailOut;
  result: QaResult;
}

function docxName(runName: string): string {
  const safe = runName.replace(/[^\w\- ().]+/g, "_").trim() || "answers";
  return `${safe}.docx`;
}

export function QaResults({ run, result }: Props) {
  const getToken = useAccessToken();
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);

  const rows = result.answers.filter((a) => filter === "all" || a.status === filter);
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const downloadDocx = async () => {
    if (!result.docx_output) return;
    setDownloading(true);
    try {
      await downloadRunOutput(await getToken(), run.run_id, result.docx_output, docxName(run.name));
    } catch (err) {
      toastApiError("Could not download the Word document", err);
    } finally {
      setDownloading(false);
    }
  };

  const filters = [
    { value: "all", label: `All (${result.answers.length})` },
    { value: "answered", label: `Answered (${result.answered})` },
    { value: "not_found", label: `Not found (${result.not_found})` },
    ...(result.failed ? [{ value: "error", label: `Failed (${result.failed})` }] : []),
  ];

  return (
    <Stack gap="md" data-testid="qa-results">
      <AdvisoryBanner />
      {result.model && (
        <Text size="sm" c="dimmed" data-testid="qa-answered-by">
          Answered by {answeredByLabel(result.model)}
          {result.model.platform ? " — pinned platform model" : ""}
        </Text>
      )}
      <Group justify="space-between">
        <SegmentedControl
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          data={filters}
          size="xs"
          aria-label="Filter by status"
        />
        <Group gap="xs">
          <ReportDownloadButton run={run} />
          <Button
            variant="light"
            leftSection={<IconFileTypeDocx size={16} />}
            onClick={() => void downloadDocx()}
            disabled={!result.docx_output}
            loading={downloading}
          >
            Download answers (Word)
          </Button>
        </Group>
      </Group>
      <Table verticalSpacing="sm" data-testid="qa-answers">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={36} aria-label="Expand" />
            <Table.Th w="30%">Question</Table.Th>
            <Table.Th>Answer</Table.Th>
            <Table.Th w={150}>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((a) => {
            const expanded = open.has(a.id);
            return (
              <Fragment key={a.id}>
                <Table.Tr>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => toggle(a.id)}
                      disabled={a.citations.length === 0}
                      aria-label={`${expanded ? "Hide" : "Show"} evidence for ${a.id}`}
                    >
                      {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                    </ActionIcon>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {a.id}. {a.question}
                    </Text>
                    {a.source_location && (
                      <Text size="xs" c="dimmed">
                        {a.source_location}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={6}>
                      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                        {a.status === "error" ? a.error : a.answer}
                      </Text>
                      {a.citations.length > 0 && (
                        <Group gap={6}>
                          {a.citations.map((c) => (
                            <CitationChip key={`${a.id}-${c.n}`} citation={c} />
                          ))}
                        </Group>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={QA_STATUS_COLOR[a.status]} variant="light">
                      {QA_STATUS_LABEL[a.status]}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
                {expanded && (
                  <Table.Tr data-testid={`qa-evidence-${a.id}`}>
                    <Table.Td />
                    <Table.Td colSpan={3}>
                      <Stack gap="xs">
                        {a.citations.map((c) => (
                          <Blockquote
                            key={`${a.id}-q-${c.n}`}
                            p="xs"
                            cite={`[${c.n}] ${citationLabel(c)}`}
                          >
                            <Text size="sm">{c.snippet}</Text>
                          </Blockquote>
                        ))}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>
      {rows.length === 0 && (
        <Text size="sm" c="dimmed">
          No questions with this status.
        </Text>
      )}
    </Stack>
  );
}
